/**
 * Local 'Modern' railing kit (Feeney task "Modern Railing Type", spec
 * "Feeney DesignRail Modern - Add to Planners").
 *
 * Temporary stand-in until the real Modern products are published on the
 * backend. Assembled at runtime the same way as the DockRail kit
 * (dockrail-local.config.ts): existing DesignRail Base Mount donors are cloned
 * with railingType switched to Modern, so posts/pickets/cables reuse donor
 * models and dimensions. The one authored piece is the Series 250 top rail
 * (new profile, spec p.2 drawing) — a localAsset placeholder generated by
 * tools/gen-modern-toprail.py. Each local entry is skipped as soon as the
 * backend has a Modern product of the same productType (and heightGroup /
 * angleType / picket variant where applicable).
 *
 * Modern rules covered by the kit itself: Base Mount only, Single corner
 * only, one top rail (Series 250), 36"/42" heights. Section span and picket
 * counts are behavior, not products — see railing-generation (maxWidth) and
 * infills.service (addSingleVerticalPicket).
 */

import { MODERN_RAILING_TYPE } from './modern.config';

// Series 250 authored placeholder (cm; matches the generated OBJ exactly)
const TOPRAIL_250 = {
  localAsset: true,
  modelObj: 'modern/toprail_250.obj',
  modelMtl: 'modern/toprail_250.mtl',
  length: 182.88, // 72" — authored 6 ft; the renderer scales x to the run length
  width: 4.9784, // 1.9600"
  height: 3.302 // 1.3000"
};

export function mergeLocalModernKit(apiProducts: any[]): any[] {
  const products = apiProducts || [];

  const hasModern = (productType: string, extra: (p: any) => boolean = () => true) =>
    products.some(
      p =>
        p &&
        p.railingType &&
        p.railingType.includes(MODERN_RAILING_TYPE) &&
        p.productType === productType &&
        extra(p)
    );

  const findDesignRailDonor = (pred: (p: any) => boolean) =>
    products.find(
      p => p && p.railingType && p.railingType.includes('DesignRail') && pred(p)
    );

  const extras = [];
  const clone = (donor: any, overrides: any) =>
    extras.push({
      ...donor,
      railingType: [MODERN_RAILING_TYPE],
      ...overrides
    });

  // Every skip predicate below mirrors the corresponding runtime lookup in
  // products.service: a backend product only suppresses a local clone if the
  // lookup would actually FIND that product. A looser check would let e.g. a
  // Modern handrail published under another handrailType kill the local 250s
  // and leave a "product is missing" toast instead of a top rail.

  // kits ship in 36" and 42" heights (spec p.2 rule 1)
  ['36', '42'].forEach(h => {
    const intPost = findDesignRailDonor(
      p =>
        p.productType === 'Intermediate Post' &&
        p.mountType === 'Base Mount' &&
        p.heightGroup === h
    );
    if (
      intPost &&
      !hasModern(
        'Intermediate Post',
        p => p.heightGroup === h && p.mountType === 'Base Mount'
      )
    ) {
      clone(intPost, { id: `local_modern_post_${h}` });
    }

    // Single corner post only (rule 2); base-mount Single donors exist for
    // both angle types, so no offset math is needed (unlike the fascia kit)
    ['Outside', 'Inside'].forEach(angle => {
      const corner = findDesignRailDonor(
        p =>
          p.productType === 'Corner Post' &&
          p.mountType === 'Base Mount' &&
          p.cornerType === 'Single' &&
          p.heightGroup === h &&
          p.angleType === angle
      );
      if (
        corner &&
        !hasModern(
          'Corner Post',
          p =>
            p.heightGroup === h &&
            p.angleType === angle &&
            p.cornerType === 'Single' &&
            p.mountType === 'Base Mount'
        )
      ) {
        clone(corner, { id: `local_modern_corner_${angle.toLowerCase()}_${h}` });
      }
    });

    // Series 250 top rail (rule 4): authored model, field donor = 200s.
    // placementHeight marks the rail BOTTOM, and the 250 profile is shorter
    // than the 200s (3.30 vs 5.08 cm), so inheriting it would drop the rail
    // top below the post top and the posts would pierce the cap. Keep the
    // rail TOP where the donor puts it instead: the cap then slides over the
    // post top by ~1.72 cm, seating on its internal ledges (~1.75 cm above
    // the cap mouth) at both 36" and 42".
    const handrail = findDesignRailDonor(
      p =>
        p.productType === 'Handrail' &&
        p.handrailType === '200s' &&
        p.heightGroup === h
    );
    if (
      handrail &&
      !hasModern('Handrail', p => p.heightGroup === h && p.handrailType === '250s')
    ) {
      clone(handrail, {
        id: `local_modern_toprail_${h}`,
        handrailType: '250s',
        ...TOPRAIL_250,
        placementHeight:
          (+handrail.placementHeight || 0) +
          (+handrail.height || 0) -
          TOPRAIL_250.height
      });
    }

    // 'modern' horizontal-slat infill: the cable donor is the placeholder
    // until Feeney provides the slat profile (spec p.8 shows only an icon)
    const cable = findDesignRailDonor(
      p => p.productType === 'Horizontal Cable' && p.heightGroup === h
    );
    if (cable && !hasModern('Horizontal Cable', p => p.heightGroup === h)) {
      clone(cable, { id: `local_modern_cable_${h}` });
    }

    // mid-span intermediate pickets (rule 5). The horizontalCable pipeline
    // requests both picket variants — clone each (see products.service:
    // getCurrentVerticalPicket / getCurrentHorizontalCableVerticalPicket).
    const picketWithBaserail = findDesignRailDonor(
      p =>
        p.productType === 'Vertical Picket' &&
        p.heightGroup === h &&
        p.verticalPicketShapeType === 'Square' &&
        p.verticalPicketForInfillWithBaserail
    );
    if (
      picketWithBaserail &&
      !hasModern(
        'Vertical Picket',
        p =>
          p.heightGroup === h &&
          !!p.verticalPicketForInfillWithBaserail &&
          p.verticalPicketShapeType === 'Square'
      )
    ) {
      clone(picketWithBaserail, { id: `local_modern_picket_baserail_${h}` });
    }

    const picketNoBaserail = findDesignRailDonor(
      p =>
        p.productType === 'Vertical Picket' &&
        p.heightGroup === h &&
        p.verticalPicketShapeType === 'Square' &&
        !p.verticalPicketForInfillWithBaserail &&
        p.mountType === 'Base Mount'
    );
    if (
      picketNoBaserail &&
      !hasModern(
        'Vertical Picket',
        p =>
          p.heightGroup === h &&
          !p.verticalPicketForInfillWithBaserail &&
          p.verticalPicketShapeType === 'Square' &&
          p.mountType === 'Base Mount'
      )
    ) {
      clone(picketNoBaserail, { id: `local_modern_picket_${h}` });
    }
  });

  // requested unconditionally by buildRailing for non-DockRail types, and
  // used by the stair runs Modern does build
  const singleCable = findDesignRailDonor(
    p => p.productType === 'Single Horizontal Cable For Stairs'
  );
  if (singleCable && !hasModern('Single Horizontal Cable For Stairs')) {
    clone(singleCable, { id: 'local_modern_single_cable' });
  }

  return extras.length ? [...products, ...extras] : products;
}



// WEBPACK FOOTER //
// ./src/app/models/2d3d/modern-local.config.ts